import { FormControl } from '@mui/material';
import { Field } from 'formik';
import { TextField } from 'formik-mui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { cascadesApi } from 'api';
import {
  BankSelect,
  FormControls,
  FormikArray,
  FormikSelect,
  FormikSelectOption,
  HelpTooltip,
  PaymentTypeSelect,
} from 'components';
import { QueryKey } from 'enums';
import { useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { formUtils } from 'utils';

type ItemProps = {
  index: number;
  cascadeOptions: FormikSelectOption[];
};

const SciPaymentConfigItem: React.FC<ItemProps> = ({
  index,
  cascadeOptions,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });

  return (
    <FormControls sx={{ flex: 1 }}>
      <PaymentTypeSelect
        name={`sciPaymentConfigs[${index}].paymentTypeId`}
        label={t('fields.sci_payment_type')}
        fullWidth
        required
      />
      <BankSelect
        label={t('fields.sci_payment_bank')}
        name={`sciPaymentConfigs[${index}].bankId`}
        noneOption
      />
      <Field
        component={TextField}
        label={t('fields.sci_payment_comment')}
        name={`sciPaymentConfigs[${index}].comment`}
        size="small"
        fullWidth
      />
      <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
        <FormikSelect
          label={t('fields.sci_payment_cascade')}
          name={`sciPaymentConfigs[${index}].cascadeId`}
          options={cascadeOptions}
          fullWidth
          noneOption
        />
        <HelpTooltip title={t('fields.sci_payment_cascade_helper')} />
      </FormControl>
    </FormControls>
  );
};

export const SciPaymentTypeConfigs: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { isAdminOrTechOperator } = useUser();

  const cascadesQuery = useQuery(QueryKey.Cascades, cascadesApi.getAll, {
    enabled: isAdminOrTechOperator,
  });
  const cascadeOptions = useMemo(
    () => formUtils.getOptions(cascadesQuery.data || [], 'name', 'id'),
    [cascadesQuery.data],
  );

  const ItemComponent = useCallback(
    ({ index }: { index: number }) => (
      <SciPaymentConfigItem index={index} cascadeOptions={cascadeOptions} />
    ),
    [cascadeOptions],
  );

  return (
    <FormikArray
      addItemLabel={t('buttons.add_sci_payment_type')}
      name="sciPaymentConfigs"
      removable
      defaultValue={{
        paymentTypeId: '',
        bankId: '',
        comment: '',
        cascadeId: '',
      }}
      ItemComponent={ItemComponent}
    />
  );
};
